import { useEffect, useRef, useState } from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineTwoToneIcon from '@mui/icons-material/ErrorOutlineTwoTone';
import { IconButton } from '@mui/material';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import { useVideoReport } from 'api/sdk';
import CircularProgressWithLabel from 'components/progress/circularProgress';
import { Repeat2 } from 'lucide-react';
import './styles.css';

interface notificationType {
  type?: string;
  value?: number;
  index?: number;
  heading?: string;
  timer?: string;
  videoQuery?: any;
  selectedConfig?: string;
  getVideoCurrentTime?: number;
  setVicdeoCurrentTime: any;
  seek: Function;
  start?: number;
  mode?: number;
}

interface notificationDataTypes {
  id: string;
  activity: string;
  sequence: any;
  start_time: number;
  end_time: number;
  setVicdeoCurrentTime: any;
  seek: Function;
  start: number;
  color: string;
  missing: number;
}

export const NotificationComponent: React.FC<notificationType> = ({
  type,
  value,
  videoQuery,
  selectedConfig,
  getVideoCurrentTime,
  setVicdeoCurrentTime,
  seek,
  mode = 0,
  start
}) => {
  const { data, refetch, isLoading }: any = useVideoReport(
    selectedConfig || '',
    videoQuery?.id || ''
  );
  const [notificationData, setNotificationData] = useState<any>([]);
  const notifyMainBlockRef = useRef<HTMLDivElement>(null);
  const lastVideoTimeRef = useRef<number | null>(null);

  // Function to scroll the main block to the bottom
  const scrollToBottom = () => {
    if (notifyMainBlockRef.current) {
      notifyMainBlockRef.current.scrollTop =
        notifyMainBlockRef.current.scrollHeight;
    }
  };

  // Scroll to bottom whenever notificationData changes
  useEffect(() => {
    scrollToBottom();
  }, [notificationData]);

  // Handle video time changes and detect backwards seeking
  useEffect(() => {
    if (!getVideoCurrentTime || !data?.data) return;

    // Detect if user has seeked backwards
    if (
      lastVideoTimeRef.current !== null &&
      getVideoCurrentTime < lastVideoTimeRef.current - 2
    ) {
      // 2-second threshold to detect significant backward seeking
      // Reset notifications based on the new time position
      const relevantNotifications = data.data.filter(
        (item: any) => item.start_time <= Math.trunc(getVideoCurrentTime)
      );
      setNotificationData(relevantNotifications);
    } else {
      // Normal forward playback - add only new notifications
      const matchingObjects = data.data.filter(
        (item: any) => item.start_time <= Math.trunc(getVideoCurrentTime)
      );

      if (matchingObjects) {
        // Filter out duplicate objects by their ID
        const newNotifications = matchingObjects.filter(
          (matchingObject: any) =>
            !notificationData.some((item: any) => item.id === matchingObject.id)
        );

        // Add new notifications to the state
        if (newNotifications.length > 0) {
          setNotificationData((prevState: any) => [
            ...prevState,
            ...newNotifications
          ]);
        }
      }
    }

    // Update the last known video time
    lastVideoTimeRef.current = getVideoCurrentTime;
  }, [getVideoCurrentTime, data?.data]);

  // Reset notifications when video starts
  useEffect(() => {
    setNotificationData([]);
    lastVideoTimeRef.current = null;
  }, [start]);

  // Fetch video report when video changes
  useEffect(() => {
    videoQuery?.id && refetch();
  }, [videoQuery?.id]);

  function setNotificationColor(data: boolean | string) {
    if (data === true) {
      return 'rgb(0,248,0)';
    } else if (data === false) {
      return 'red';
    } else {
      return 'orange';
    }
  }

  function formatTime(seconds: number) {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;

    const formattedHours = hours.toString().padStart(2, '0');
    const formattedMinutes = minutes.toString().padStart(2, '0');
    const formattedSeconds = remainingSeconds.toString().padStart(2, '0');

    return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
  }

  function calculateTaskPercent(taskVallue: notificationDataTypes) {
    const percentValue =
      getVideoCurrentTime &&
      (getVideoCurrentTime < taskVallue?.end_time
        ? ((getVideoCurrentTime - taskVallue?.start_time) * 100) /
          (taskVallue?.end_time - taskVallue?.start_time)
        : 100);
    return percentValue && percentValue < 0 ? 0 : percentValue;
  }

  return (
    <>
      {notificationData.length > 0 &&
        notificationData[notificationData.length - 1].ncycles !== undefined && (
          <div className="notify-container">
            <Repeat2
              style={{
                height: '30px',
                width: '30px',
                color: 'white'
              }}
            />
            <div className="notify-main-data">
              <p style={{ margin: 0, padding: 0 }}>
                Cycle:{' '}
                <span>
                  {notificationData[notificationData.length - 1]?.ncycles || 0}
                </span>
                <IconButton
                  disableRipple
                  style={{
                    color:
                      notificationData[notificationData.length - 1]
                        ?.sequence === 1
                        ? 'green'
                        : 'red',
                    alignContent: 'center'
                  }}
                >
                  {notificationData[notificationData.length - 1]?.sequence ==
                  1 ? (
                    <CheckCircleIcon />
                  ) : (
                    <CancelRoundedIcon />
                  )}
                </IconButton>
              </p>
            </div>
          </div>
        )}
      <div className="notify-main-block" ref={notifyMainBlockRef}>
        {notificationData.map((data: notificationDataTypes, index: number) => {
          return (
            <div
              className="notify-container"
              key={data.id}
              onClick={() => seek(data?.start_time)}
            >
              <CircularProgressWithLabel
                style={{
                  height: '40px',
                  width: '40px',
                  color: data?.color
                    ? data?.color
                    : setNotificationColor(data?.sequence)
                }}
                variant="determinate"
                value={
                  calculateTaskPercent(data) == 100 ? (
                    <IconButton
                      disableRipple
                      style={{
                        color: data?.color
                          ? data?.color
                          : setNotificationColor(data?.sequence),
                        alignContent: 'center'
                      }}
                    >
                      {data?.missing === 0 ? (
                        <CheckCircleIcon />
                      ) : (
                        <ErrorOutlineTwoToneIcon />
                      )}
                    </IconButton>
                  ) : (
                    calculateTaskPercent(data)
                  )
                }
                size={60}
              />
              <div className="notify-main-data">
                <div className="notification-head">
                  <div
                    style={{
                      fontWeight: 'bold',
                      width: '100%',
                      display: 'block',
                      boxSizing: 'border-box',
                      whiteSpace: 'normal',
                      wordWrap: 'break-word',
                      marginBottom: '2px'
                    }}
                  >
                    {data?.activity}
                  </div>
                </div>
                <div className="timer">
                  {data.sequence === 'missing'
                    ? 'Sequence Missed'
                    : mode === 0
                    ? formatTime(data?.end_time - data?.start_time)
                    : null}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};

export const NotificationList = ({
  videoQuery,
  selectedConfig,
  getVideoCurrentTime,
  setVicdeoCurrentTime,
  seek,
  mode,
  start
}: notificationType) => {
  return (
    <div className="notification-bar">
      <NotificationComponent
        heading={'Crimp contact onto wire 2'}
        getVideoCurrentTime={getVideoCurrentTime}
        index={1}
        type={'success'}
        value={100}
        setVicdeoCurrentTime={setVicdeoCurrentTime}
        timer={'00:00:24'}
        videoQuery={videoQuery}
        selectedConfig={selectedConfig}
        seek={seek}
        start={start}
        mode={mode}
      />
    </div>
  );
};
